import * as XLSX from 'xlsx';

function toArrayBuffer(input) {
  if (input instanceof ArrayBuffer) return Promise.resolve(input);
  if (ArrayBuffer.isView(input)) return Promise.resolve(input.buffer);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(input);
  });
}

function stripBom(value) {
  if (typeof value !== 'string' || value.length === 0) return value;
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

function normalizeCell(value) {
  if (value === undefined || value === '') return null;
  return value;
}

const nextTask = () => new Promise((resolve) => setTimeout(resolve));

// ── Worker per a la lectura pesada de SheetJS (no bloqueja el thread principal) ──
// Es crea de forma mandrosa i es reutilitza. Si l'entorn no té Worker (tests/node)
// o la construcció falla, callWorker torna null i s'usa el camí síncron de reserva.
let workerSingleton;
let workerUnavailable = false;
let reqSeq = 0;

function getWorker() {
  if (workerUnavailable) return null;
  if (workerSingleton) return workerSingleton;
  if (typeof Worker === 'undefined') { workerUnavailable = true; return null; }
  try {
    workerSingleton = new Worker(new URL('./xlsx.worker.js', import.meta.url), { type: 'module' });
    return workerSingleton;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

// Torna una Promise amb la resposta del worker, o null si no hi ha worker (reserva).
function callWorker(op, buffer, sheet) {
  const worker = getWorker();
  if (!worker) return null;
  const id = ++reqSeq;
  return new Promise((resolve, reject) => {
    const onMessage = (e) => {
      if (!e.data || e.data.id !== id) return;
      cleanup();
      if (e.data.ok) resolve(e.data); else reject(new Error(e.data.error || 'Error llegint l\'XLSX'));
    };
    const onError = (err) => { cleanup(); reject(err instanceof Error ? err : new Error('Error al worker XLSX')); };
    function cleanup() {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    }
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    // Transferim el buffer (no es copia); no el reutilitzem al thread principal.
    worker.postMessage({ id, op, buffer, sheet }, [buffer]);
  });
}

export async function listSheets(input) {
  const buffer = await toArrayBuffer(input);
  const p = callWorker('sheets', buffer);
  if (p) return (await p).sheetNames;
  // Reserva síncrona (sense Worker): només noms de fulls, ràpid.
  const workbook = XLSX.read(buffer, { type: 'array', bookSheets: true });
  return workbook.SheetNames;
}

export async function parseXlsx(input, options = {}, onProgress = () => {}) {
  onProgress({ phase: 'reading' });
  const buffer = await toArrayBuffer(input);
  onProgress({ phase: 'parsing' });

  const p = callWorker('parse', buffer, options.sheet);
  if (p) {
    const res = await p;
    return {
      headers: res.headers,
      rows: res.rows,
      sheetName: res.sheetName,
      sheetNames: res.sheetNames,
      encoding: 'utf-8',
    };
  }

  // ── Reserva síncrona (tests/node sense Worker): manté el comportament anterior ──
  await nextTask();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetNames = workbook.SheetNames;
  if (sheetNames.length === 0) {
    return { headers: [], rows: [], sheetName: null, sheetNames: [], encoding: 'utf-8' };
  }
  const sheetName = options.sheet && sheetNames.includes(options.sheet) ? options.sheet : sheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: null, raw: true });
  if (matrix.length === 0) {
    return { headers: [], rows: [], sheetName, sheetNames, encoding: 'utf-8' };
  }
  const headerRow = matrix.shift() || [];
  const headers = headerRow.map((h, idx) => stripBom(String(h ?? `col_${idx + 1}`)).trim());
  const rows = [];
  const total = matrix.length;
  for (let i = 0; i < total; i++) {
    const row = matrix[i];
    if (Array.isArray(row) && row.some((cell) => cell !== null && cell !== '')) {
      const out = new Array(headers.length).fill(null);
      for (let j = 0; j < headers.length; j++) out[j] = normalizeCell(row[j]);
      rows.push(out);
    }
    if ((i & 8191) === 0) { onProgress({ phase: 'parsing', processed: i, total }); await nextTask(); }
  }
  onProgress({ phase: 'parsing', processed: total, total });
  return { headers, rows, sheetName, sheetNames, encoding: 'utf-8' };
}
